import { Fragment } from "react";

import { APPRAISAL_MONEY as MONEY, COMPENSATION_COMPONENTS } from "../compensation-components.ts";
import type { CompensationComponentKey } from "../compensation-components.ts";
import { annualAmountToRoundedMonthly } from "../appraisal-annexure-utils.ts";
import { TextField } from "../../../ui/index.ts";
import styles from "../employee-appraisal.module.css";

export type AppraisalAnnexureReviewData = {
  components: Record<(typeof COMPENSATION_COMPONENTS)[number]["key"], number> & { annualFixedComponent: number };
  monthlyGross: number;
  annualGross: number;
  monthlyBenefits: number;
  annualBenefits: number;
  monthlyCtc: number;
  annualCtc: number;
  esicEligible: boolean;
  annualComponentEnabled?: boolean;
  remarks?: string;
};

type Props = {
  data: AppraisalAnnexureReviewData;
  editable?: boolean;
  onComponentChange?: (key: CompensationComponentKey, value: number) => void;
  onMonthlyGrossChange?: (value: number) => void;
  onAnnualFixedComponentChange?: (value: number) => void;
};

function numericValue(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
}

export function AppraisalAnnexureReview({ data, editable = false, onComponentChange, onMonthlyGrossChange, onAnnualFixedComponentChange }: Props) {
  const displayedComponents = COMPENSATION_COMPONENTS.filter((component) =>
    data.esicEligible || !component.key.startsWith("esic"),
  );
  return <>
    <div className={styles.compensationTableWrap}><table className={styles.compensationTable}>
      <thead><tr><th>#</th><th>Salary Component</th><th>Monthly (INR)</th><th>Annual (INR)</th></tr></thead>
      <tbody>{displayedComponents.map((component, index) => <Fragment key={component.key}>
        {index === 0 || displayedComponents[index - 1]?.group !== component.group ? <tr className={styles.componentGroupRow}><td colSpan={4}>{component.group}</td></tr> : null}
        <tr><td>{index + 1}</td><td><strong>{component.label}</strong></td><td>{editable ? <TextField className={component.editable ? styles.editableAmountField : undefined} id={`review-${component.key}`} type="number" min="0" step="1" aria-label={`${component.label} monthly amount`} value={String(data.components[component.key])} readOnly={!component.editable} onChange={component.editable ? (event) => onComponentChange?.(component.key, numericValue(event.target.value)) : undefined} /> : MONEY.format(data.components[component.key])}</td><td>{MONEY.format(component.key === "gratuity" ? data.components.basic / 2 : data.components[component.key] * 12)}</td></tr>
        {component.key === "internetReimbursement" ? <tr className={styles.subtotalRow}><td>A+B+C</td><td><strong>Total (A + B + C)</strong></td><td>{editable ? <TextField className={styles.editableAmountField} id="review-monthly-gross" type="number" min="0" step="1" aria-label="Monthly A plus B plus C total" value={String(data.monthlyGross)} onChange={(event) => onMonthlyGrossChange?.(numericValue(event.target.value))} /> : MONEY.format(data.monthlyGross)}</td><td>{MONEY.format(data.annualGross)}</td></tr> : null}
        {component.key === (data.esicEligible ? "esicEmployerPart" : "pfEmployerPart") ? <>
          <tr className={styles.subtotalRow}><td>D</td><td><strong>Total (D)</strong></td><td>{MONEY.format(data.monthlyBenefits)}</td><td>{MONEY.format(data.annualBenefits)}</td></tr>
          <tr className={styles.componentGroupRow}><td colSpan={4}>E. Annual Fixed Component</td></tr>
          <tr><td>E</td><td><strong>Annual Fixed Component</strong></td><td>{MONEY.format(annualAmountToRoundedMonthly(data.components.annualFixedComponent))}</td><td>{editable ? <TextField className={styles.editableAmountField} id="review-annual-fixed-component" type="number" min="0" step="1" aria-label="Annual Fixed Component annual amount" value={String(data.components.annualFixedComponent)} onChange={(event) => onAnnualFixedComponentChange?.(numericValue(event.target.value))} /> : MONEY.format(data.components.annualFixedComponent)}</td></tr>
        </> : null}
      </Fragment>)}</tbody>
      <tfoot><tr className={styles.grossCtcRow}><td>F</td><td><strong>Gross CTC (A + B + C + D + E)</strong></td><td>{MONEY.format(data.monthlyCtc)}</td><td>{MONEY.format(data.annualCtc)}</td></tr></tfoot>
    </table></div>
    <div className={styles.annexureReviewMeta}><span><strong>Annual Component:</strong> {data.annualComponentEnabled ? "Yes" : "No"}</span><span><strong>Remarks:</strong> {data.remarks || "-"}</span></div>
  </>;
}
